'use client';

import { useCopilotReadable } from '@copilotkit/react-core';
import { useState, useRef, useEffect, ChangeEvent, useMemo, useCallback } from 'react';
import { useRouter } from 'next/navigation';

// Constants
import { KEYBOARD_EVENT, SEARCH_PLACEHOLDER } from '@shared/constants';

// Components
import CloseIcon from '@shared/icons/CloseIcon';
import { Button, Separator, CustomDialog } from '@shared/ui';

// Hooks
import useResumeData from '@/hooks/useResumeData';
import { useMentionUsers } from '@/hooks/useMentionUsers';
import { useMentionProjects } from '@/hooks/useMentionProjects';

// Types
import { ActionItemType, ActionKind, ActivityType, Project } from '@shared/types';
import { GetToken, UserProfile } from '@/types';

// Helper
import { getConfirmationConfig, groupOptionsByGroup, handleActionClick as handleActionClickHelper, stripJsonSuffix } from './helpers';

// Utils
import { combineClasses } from '@shared/utils';

// Store
import { useProjects, useUsers } from '@/stores';
import { ConfirmationActionModal } from '../ChatBot/ConfirmationActionModal';
import { ThinkingIndicator } from './ThinkingIndicator';
import { ChatbotResponse } from './ChatbotResponse';
import { UserMentionDropdown } from '../UserMentionDropdown';
import { handleMentionSelection } from '../UserMentionDropdown/helpers';
import { ProjectMentionDropdown } from '../ProjectMentionDropdown';
import { handleProjectMentionSelection } from '../ProjectMentionDropdown/helpers';
import { CHATBOT_MESSAGES } from '@/constants';
export interface SearchVisibleMessage {
  id?: string;
  role?: string;
  content?: unknown;
}
interface SearchFormProps {
  value?: string;
  actionItems?: ActionItemType[];
  isLoading?: boolean;
  onInputChange?: (value: string) => void;
  onSubmitSearch?: (value: string) => void;
  onClose?: () => void;
  onResetResult?: () => void;
  onClearKeyword?: () => void;
  onProceedingAction?: (isLoading: boolean) => void;
  onMentionDropdownVisibilityChange?: (isVisible: boolean) => void;
  requestConfig?: {
    getToken: GetToken;
    userEmail: string;
  };
  onSuccessAction?: () => void;
  isAISupport?: boolean;
  visibleMessages?: SearchVisibleMessage[];
}
const DEFAULT_OPTION_INDEX = -1;
const formalizeMsgContent = (content: string, actionLabel: string) => {
  const sanitized = content?.replace(/\s?\[.*?\]\(#\)\s?/g, '');
  const hasTSFormat = content.includes('```ts');
  if (hasTSFormat && actionLabel) {
    return CHATBOT_MESSAGES.SEARCH_SUCCESSFULLY(actionLabel);
  }
  return sanitized || CHATBOT_MESSAGES.SEARCH_SUCCESSFULLY(actionLabel);
};
const getTextContent = (message: SearchVisibleMessage) => typeof message.content === 'string' ? message.content : '';
export const SearchForm = ({
  value = '',
  actionItems = [],
  isLoading = false,
  onInputChange,
  onClose,
  requestConfig,
  onSuccessAction,
  onProceedingAction = () => {},
  onClearKeyword = () => {},
  onResetResult = () => {},
  isAISupport = false,
  onSubmitSearch = () => {},
  visibleMessages = []
}: SearchFormProps) => {
  const router = useRouter();
  // Add state to track which item is currently loading
  const [searchQuery, setSearchQuery] = useState(value);
  const [optionExecuteBefore, setOptionExecuteBefore] = useState('');
  const [cursorPosition, setCursorPosition] = useState<number | null>(null);
  const [selectedOptionIndex, setSelectedOptionIndex] = useState(DEFAULT_OPTION_INDEX);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const [needConfirmation, saveConfirmation] = useState(false);
  const {
    Template
  } = useResumeData();
  const users = useUsers();
  const projects = useProjects();
  if (isAISupport) {
    /**
     * CopilotKit integration
     */
    // eslint-disable-next-line react-hooks/rules-of-hooks
    useCopilotReadable({
      description: 'The context include users and projects context',
      value: JSON.stringify({
        users: users?.map(({
          name,
          username,
          status
        }) => `username:${username}-name:${name}-status:${status}`),
        projects: projects.map(({
          projectInfo
        }) => `code:${projectInfo.code}-name:${projectInfo.name}-status:${projectInfo.status}-archived:${projectInfo.archived}`)
      })
    });
  }
  const {
    users: mentionUsers,
    isLoading: isMentionLoading,
    selectedUserIndex,
    isMentionActive: isUserMentionActive,
    selectedUserRef: mentionSelectedUserRef,
    dropdownRef: mentionDropdownRef,
    detectMention,
    selectUser,
    handleKeyDown: handleMentionKeyDown,
    closeMention,
    checkForExactMatch,
    updateSelectedUserIndex
  } = useMentionUsers();

  // Initialize mention projects hook
  const {
    projects: mentionProjects,
    isLoading: isProjectMentionLoading,
    selectedProjectIndex,
    isMentionActive: isProjectMentionActive,
    selectedProjectRef: mentionSelectedProjectRef,
    dropdownRef: projectMentionDropdownRef,
    detectMention: detectProjectMention,
    selectProject,
    handleKeyDown: handleProjectMentionKeyDown,
    closeMention: closeProjectMention,
    checkForExactMatch: checkForExactProjectMatch,
    updateSelectedProjectIndex
  } = useMentionProjects({
    debounceTime: 300
  });

  // Group options by group
  const groupedOptions = useMemo(() => groupOptionsByGroup(actionItems), [actionItems]);
  const confirmationConfig = useMemo(() => {
    if (actionItems && actionItems[0]) {
      return getConfirmationConfig(actionItems[0]);
    }
  }, [actionItems]);

  // Create a flat array of all options for keyboard navigation
  const allOptions = useMemo(() => Object.values(groupedOptions).flat(), [groupedOptions]);
  const groupOptionsList = Object.entries(groupedOptions);
  const validationItem = actionItems.find(item => item.kind === ActionKind.VALIDATION);
  const handleSubmitSearch = useCallback(() => {
    onSubmitSearch?.(searchQuery);
    setOptionExecuteBefore('');
  }, [onSubmitSearch, searchQuery]);

  // Handle user selection from mention dropdown (mouse click)
  const handleUserSelect = useCallback((user: UserProfile) => {
    const selectedUser = selectUser(user);
    const {
      mentionText,
      replaceStart,
      replaceEnd
    } = selectedUser;

    // Replace partial mention with selected user's mention text
    handleMentionSelection(mentionText, replaceStart, replaceEnd, searchQuery, setSearchQuery, setCursorPosition, closeMention, onInputChange);
    handleSubmitSearch();
  }, [selectUser, searchQuery, closeMention, onInputChange, handleSubmitSearch]);

  // Handle project selection from mention dropdown (mouse click)
  const handleProjectSelect = useCallback((project: Project) => {
    const selectedProject = selectProject(project);
    const {
      mentionText,
      replaceStart,
      replaceEnd
    } = selectedProject;

    // Replace partial mention with selected project's mention text
    handleProjectMentionSelection(mentionText, replaceStart, replaceEnd, searchQuery, setSearchQuery, setCursorPosition, closeProjectMention, onInputChange);
    handleSubmitSearch();
  }, [selectProject, searchQuery, closeProjectMention, onInputChange, handleSubmitSearch]);

  // Focus input and position cursor after mention selection
  useEffect(() => {
    if (cursorPosition !== null && searchInputRef.current) {
      searchInputRef.current.focus();
      searchInputRef.current.setSelectionRange(cursorPosition, cursorPosition);
      setTimeout(() => {
        detectMention(searchQuery, cursorPosition);
        detectProjectMention(searchQuery, cursorPosition);
      }, 0);
      setCursorPosition(null); // Reset after positioning
    }
  }, [cursorPosition, searchQuery, detectMention, detectProjectMention]);

  // Auto-highlight exact username match in mention dropdown
  useEffect(() => {
    if (isUserMentionActive) {
      const exactMatch = checkForExactMatch();
      if (exactMatch) {
        const exactMatchIndex = mentionUsers.findIndex(user => user.id === exactMatch.id);
        if (exactMatchIndex !== -1) {
          updateSelectedUserIndex(exactMatchIndex);
        }
      }
    }
  }, [isUserMentionActive, checkForExactMatch, mentionUsers, updateSelectedUserIndex]);

  // Auto-highlight exact project match in project mention dropdown
  useEffect(() => {
    if (isProjectMentionActive) {
      const exactMatch = checkForExactProjectMatch();
      if (exactMatch) {
        const exactMatchIndex = projects.findIndex(({
          projectInfo
        }) => projectInfo.id === exactMatch.projectInfo.id);
        if (exactMatchIndex !== -1) {
          updateSelectedProjectIndex(exactMatchIndex);
        }
      }
    }
  }, [isProjectMentionActive, checkForExactProjectMatch, projects, updateSelectedProjectIndex]);
  const handleActionClick = useCallback(async (item: ActionItemType) => {
    onProceedingAction(true);
    if (item?.type && [ActivityType.PROJECT_ARCHIVE, ActivityType.USER_DEACTIVATE].includes(item?.type)) {
      saveConfirmation(true);
    } else {
      await handleActionClickHelper({
        item,
        router,
        requestConfig,
        onSuccessAction,
        onClose,
        setSearchQuery,
        setCursorPosition
      });
      // Clean action items after action is handled
      onResetResult();
    }
  },
  // eslint-disable-next-line react-hooks/exhaustive-deps
  [Template, onClose, onSuccessAction, requestConfig, router, onProceedingAction]);
  useEffect(() => {
    const executeAction = async () => {
      if (groupOptionsList.length > 0 && !isLoading && !validationItem) {
        const result = groupOptionsList[0];
        const detectedAction = result?.[1][0];
        if (detectedAction?.label === optionExecuteBefore) return;
        setOptionExecuteBefore(detectedAction?.label || '');
        if (detectedAction?.type && [ActivityType.PROJECT_ARCHIVE, ActivityType.USER_DEACTIVATE].includes(detectedAction?.type)) {
          saveConfirmation(true);
          onProceedingAction(true);
        } else {
          handleActionClick(detectedAction as ActionItemType);
        }
      }
    };
    executeAction();
  }, [groupOptionsList, handleActionClick, isLoading, validationItem, searchQuery, optionExecuteBefore, onProceedingAction]);
  const handleSubmitAction = async () => {
    saveConfirmation(false);
    const result = groupOptionsList[0];
    if (result?.[1][0]) {
      await handleActionClickHelper({
        item: result?.[1][0],
        router,
        requestConfig,
        onSuccessAction,
        onClose,
        setSearchQuery
      });
    }
  };

  // Handle keyboard events: mention events → regular navigation
  const handleKeyDown = (event: React.KeyboardEvent) => {
    // Handle user mention events first (higher priority)
    const mentionHandled = handleMentionKeyDown(event);
    if (mentionHandled) {
      // If user selected someone (Enter/Space), replace partial mention with full mention
      if (typeof mentionHandled === 'object' && mentionHandled.mentionText) {
        const {
          mentionText,
          replaceStart,
          replaceEnd
        } = mentionHandled;
        handleMentionSelection(mentionText, replaceStart, replaceEnd, searchQuery, setSearchQuery, setCursorPosition, closeMention, onInputChange);
      }
    }

    // Handle project mention events if user mention didn't handle it
    const projectMentionHandled = handleProjectMentionKeyDown(event);
    if (projectMentionHandled) {
      // If user selected a project (Enter/Space), replace partial mention with full mention
      if (typeof projectMentionHandled === 'object' && projectMentionHandled.mentionText) {
        const {
          mentionText,
          replaceStart,
          replaceEnd
        } = projectMentionHandled;
        handleProjectMentionSelection(mentionText, replaceStart, replaceEnd, searchQuery, setSearchQuery, setCursorPosition, closeProjectMention, onInputChange);
        handleSubmitSearch();
      }
    }
    if (event.key === KEYBOARD_EVENT.ENTER) {
      handleSubmitSearch();
      return;
    }
  };

  // Handle input changes and trigger mention detection
  const handleSearchInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    const newValue = event.target.value;
    const cursorPosition = event.target.selectionStart || 0;
    setSearchQuery(newValue);
    onInputChange?.(newValue);

    // Detect mentions when input changes
    detectMention(newValue, cursorPosition);
    detectProjectMention(newValue, cursorPosition);
  };

  // Clear input and reset all states
  const handleClearInput = () => {
    setSearchQuery('');
    setSelectedOptionIndex(DEFAULT_OPTION_INDEX);
    closeMention();
    closeProjectMention();
    onClearKeyword();
    searchInputRef.current?.focus();
  };

  // Reset selection when search changes or options change
  if (selectedOptionIndex >= allOptions.length && allOptions.length > 0) {
    setSelectedOptionIndex(DEFAULT_OPTION_INDEX);
  }
  const handleCloseDialog = () => {
    handleClearInput();
    onClose?.();
  };
  const handleCloseConfirmDialog = () => {
    saveConfirmation(false);
    onProceedingAction(false);
  };
  const streamingMessage = useMemo(() => visibleMessages.filter(msg => msg.role !== 'user' && !!getTextContent(msg)).map(msg => ({
    content: stripJsonSuffix(getTextContent(msg)),
    msg
  })).filter(({
    content
  }) => !!content).slice(-1).map(({
    msg,
    content
  }) => ({
    content: formalizeMsgContent(content, actionItems[0]?.label || ''),
    id: msg.id || ''
  })),
  // Only get the final message
  [visibleMessages, actionItems]);
  return <div id="search-form" onKeyDown={handleKeyDown} role="presentation" className="w-full" data-sentry-component="SearchForm" data-sentry-source-file="index.tsx">
      <div className={combineClasses('flex items-center w-full relative', 'pr-7 pl-7.5 my-5')}>
        <div className="flex-1 relative">
          <div className="relative">
            <input ref={searchInputRef} role="combobox" aria-expanded={true} aria-haspopup="listbox" aria-controls={'user-mention-dropdown'} aria-describedby="search-instructions" aria-label="Search for actions, mention users with @ or projects with #" className={combineClasses('flex min-h-[35px] w-full rounded-none border-0 outline-none py-3 p-0', 'text-xs font-normal placeholder:text-frost-500 bg-transparent', 'focus-visible:outline-none focus-visible:ring-0 focus-visible:ring-transparent hover:ring-0', 'relative z-10')} placeholder={SEARCH_PLACEHOLDER} value={searchQuery} onChange={handleSearchInputChange} />
          </div>
        </div>

        {searchQuery.length > 0 && <Button size="icon" className={combineClasses('min-w-0 p-0 sm:p-2', 'bg-transparent hover:bg-transparent')} onClick={handleClearInput}>
            <CloseIcon className="stroke-frost-500 size-4" />
          </Button>}

        <Button data-testid="search-shortcut" size="sm" aria-hidden="true" aria-label="Keyboard shortcut: Esc" onClick={handleCloseDialog} className={combineClasses('hidden sm:flex sm:ml-2.5 hover:bg-transparent', 'flex-center w-15 h-6 rounded-md px-0 min-w-0', 'bg-white text-frost-900 border border-border-500')} data-sentry-element="Button" data-sentry-source-file="index.tsx">
          Esc
        </Button>
      </div>

      {/* User Mention Dropdown */}
      {isUserMentionActive && <UserMentionDropdown ref={mentionDropdownRef} users={mentionUsers} isLoading={isMentionLoading} selectedIndex={selectedUserIndex} onUserSelect={handleUserSelect} selectedUserRef={mentionSelectedUserRef} isVisible={isUserMentionActive} />}

      {/* Project Mention Dropdown */}
      {isProjectMentionActive && <ProjectMentionDropdown ref={projectMentionDropdownRef} projects={mentionProjects} isLoading={isProjectMentionLoading} selectedIndex={selectedProjectIndex} onProjectSelect={handleProjectSelect} selectedProjectRef={mentionSelectedProjectRef} isVisible={isProjectMentionActive} />}

      {(streamingMessage.length > 0 || isLoading) && <>
          <Separator />
          <div className="pt-2.5 pb-3.5">
            <div className="search-bar-result max-h-modal-content md:max-h-modal-content-md overflow-y-auto small-scrollbar">
              {isLoading ? <ThinkingIndicator /> : streamingMessage.map(msg => <ChatbotResponse msg={msg} key={msg.id} />)}
            </div>
          </div>
        </>}

      <CustomDialog open={needConfirmation} onOpenChange={handleCloseConfirmDialog} className={combineClasses('sm:max-w-md active:ring-0', 'focus:ring-0 focus-visible:ring-0 focus-within:ring-0')} titleClassName="text-xl font-semibold text-gray-900 mb-2" overlayClassName="z-50 bg-black/50" title="Confirmation" data-sentry-element="CustomDialog" data-sentry-source-file="index.tsx">
        <ConfirmationActionModal hasShadow={false} message={confirmationConfig?.message || ''} confirmLabel={confirmationConfig?.confirmLabel} cancelLabel={confirmationConfig?.cancelLabel} onCancel={handleCloseConfirmDialog} onSubmit={handleSubmitAction} data-sentry-element="ConfirmationActionModal" data-sentry-source-file="index.tsx" />
      </CustomDialog>
    </div>;
};